import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container, Typography, Box, TextField, Button,
  InputAdornment, IconButton, alpha, Switch, Chip, Tab, Tabs,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  Numbers as NumbersIcon,
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as ReplaceIcon,
  AutoAwesome as AutoAwesomeIcon,
  Person as PersonIcon,
  AccountBalanceWallet as TreasurerIcon,
  TaskAlt as ApproveIcon,
  Block as RejectIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useAuth } from '../../../contexts/AuthContext';
import FeedbackMessage from '../../common/FeedbackMessage';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { SUCCESS } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { canManageAllClaims, claimEditBlockedMessage } from '../../../utils/financialPermissions';
import SearchableSelect from '../../common/SearchableSelect';
import { makePickerSlots } from '../../common/formFieldSx';
import {
  applyApiValidationErrors,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';
import {
  fetchClaimReceiptPreviewBlob,
  receiptPathIsPdf,
} from '../../../utils/protectedFilePreview';
import { signedPreviewUrlIsUsable } from '../../../utils/storageUrl';
import {
  buildClaimTitleFromLineItems,
  sumClaimLineItemAmounts,
} from '../../../utils/claimLineItems';
import ClaimLineItemsHierarchy from './ClaimLineItemsHierarchy';

const claimHasSavedPurchaseReceipt = (d) => Boolean(
  d?.purchase_receipt_preview_url
  || d?.purchase_receipt_path
  || d?.purchase_receipt_url
  || d?.receipt_url
  || d?.receipt_path
);
const claimHasSavedReimbursementReceipt = (d) => Boolean(
  d?.reimbursement_receipt_preview_url
  || d?.reimbursement_receipt_path
  || d?.reimbursement_receipt_url
  || d?.payment_receipt_url
  || d?.payment_receipt_path
);

const LARAVEL_FIELD_TO_FORM = {
  item_bought: 'claim_title',
  payment_method: 'club_payment_method',
};

const MEMBER_PAYOUT_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'tng', label: 'TNG' },
];

const CLUB_PAYOUT_OPTIONS = [...MEMBER_PAYOUT_OPTIONS];

const CLAIM_VALIDATION_FIELDS = new Set([
  'claim_title', 'line_items', 'claimed_for_user_id', 'full_name', 'amount', 'payment_method', 'purchase_payment_method',
  'reimbursement_payment_method', 'company_id', 'company_name', 'description',
  'purchase_payment_date', 'payment_date', 'receipt_number', 'payment_account',
  'status', 'rejection_reason', 'reimbursement_receipt', 'receipt_file', 'budget_id',
  'member_payout_method', 'club_payment_method',
]);

const formatBudgetOptionLabel = (b) => {
  const item = b?.item || `Budget #${b?.id}`;
  const price = b?.amount != null && b.amount !== '' ? ` · RM ${Number(b.amount).toLocaleString()}` : '';
  return `${item}${price}`;
};

const REVIEW_DRAFT_FIELDS = ['status', 'rejection_reason', 'is_paid', 'club_payment_method', 'budget_id', 'is_claimable'];
const helperSlot = (msg) => msg || ' ';
const pickReviewDraftSlice = (data) => {
  if (!data || typeof data !== 'object') return {};
  const out = {};
  REVIEW_DRAFT_FIELDS.forEach((key) => { if (data[key] !== undefined) out[key] = data[key]; });
  return out;
};

const emptyLineItem = () => ({ name: '', amount: '' });

const normalizeLineItemsFromApi = (raw, fallbackTitle = '') => {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((row) => ({
      name: row?.name ?? row?.item ?? '',
      amount: row?.amount != null && row.amount !== '' ? String(row.amount) : '',
    }));
  }
  const title = String(fallbackTitle || '').trim();
  if (title) return [{ name: title, amount: '' }];
  return [emptyLineItem()];
};

const sumLineItemAmounts = (items) => {
  const total = sumClaimLineItemAmounts(items);
  return total != null ? total.toFixed(2) : null;
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const AMBER = '#FBBF24';
const ROSE  = '#F87171';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const SURFACE_CARD = '#0d1b30';

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#94A3B8';
const TEXT_HINT  = '#475569';

// Disabled field tokens
// Read-only claim data still needs to be easy to scan during review. MUI's
// default disabled treatment is intentionally faint, so use opaque colours
// that communicate the state through the surface and border instead.
const FIELD_DISABLED_BG     = 'rgba(15,31,56,0.72)';
const FIELD_DISABLED_BORDER = 'rgba(100,116,139,0.52)';
const FIELD_DISABLED_TEXT   = '#B8C4D6';
const FIELD_DISABLED_LABEL  = '#B6C2D2';
const FIELD_DISABLED_ICON   = '#6F829E';
const FIELD_DISABLED_HINT   = '#94A3B8';

// ─── Global CSS ───────────────────────────────────────────────────────────────
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:opsz,wght@9..40,300;400;500;600&display=swap');

  @keyframes glow-ring {
    from { box-shadow: 0 0 0 0 rgba(56,189,248,0.5); }
    to   { box-shadow: 0 0 0 5px rgba(56,189,248,0); }
  }
  @keyframes fade-up {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes file-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes ocr-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.45; }
  }
  @keyframes tab-slide {
    from { opacity: 0; transform: translateX(10px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes banner-drop {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .MuiOutlinedInput-input, .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
  .claim-tab-panel { animation: tab-slide 0.22s ease both; }
`;

const PICKER_CSS = `
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.18) !important;
    border-radius: 14px !important;
    box-shadow: 0 12px 40px rgba(0,0,0,0.7) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.55) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.63rem !important;
    font-weight: 600 !important;
    letter-spacing: 1.5px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.84rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.18s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.12) !important;
    color: #38BDF8 !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: #38BDF8 !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 14px rgba(56,189,248,0.5) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 1.5px solid rgba(56,189,248,0.5) !important;
    color: #38BDF8 !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.65) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.08) !important;
  }
`;

// ─── Shared input sx ──────────────────────────────────────────────────────────
const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.9),
    background: alpha(SURFACE_UP, 0.65),
    backdropFilter: 'blur(6px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.14), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.32) },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(NEON, 0.1)}`,
      animation: 'glow-ring 0.35s ease',
    },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.6), borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: `${alpha(ROSE, 0.7)} !important` },
    '&.Mui-error:hover fieldset': { borderColor: `${ROSE} !important` },
    '&.Mui-error.Mui-focused fieldset': {
      borderColor: '#EF4444 !important',
      borderWidth: '1.5px',
      boxShadow: `0 0 0 3px ${alpha('#EF4444', 0.1)}`,
    },
    '&.Mui-disabled': {
      background: FIELD_DISABLED_BG,
      backdropFilter: 'none',
      cursor: 'not-allowed',
      opacity: 1,
      '& fieldset': { borderColor: `${FIELD_DISABLED_BORDER} !important` },
      '&:hover fieldset': { borderColor: `${FIELD_DISABLED_BORDER} !important` },
      '& .MuiInputBase-input, & .MuiSelect-select': {
        color: `${FIELD_DISABLED_TEXT} !important`,
        WebkitTextFillColor: `${FIELD_DISABLED_TEXT} !important`,
        opacity: '1 !important',
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root, & .MuiSelect-icon': {
        color: `${FIELD_DISABLED_ICON} !important`,
        opacity: '1 !important',
      },
    },
  },
  '& .MuiInputBase-input': {
    color: `${alpha(TEXT_BODY, 0.9)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.9)} !important`,
    '&::placeholder': { color: alpha(NEON, 0.25), opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    color: alpha(TEXT_MUTED, 0.6),
  },
  '& .MuiInputLabel-root.Mui-error': { color: `${ROSE} !important` },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: `${FIELD_DISABLED_LABEL} !important`,
    opacity: '1 !important',
  },
  '& label.Mui-focused': { color: alpha(NEON, 0.8), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.38), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.38) },
  '& .MuiFormHelperText-root': {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.67rem',
    letterSpacing: 0.2,
    color: alpha(TEXT_MUTED, 0.5),
    marginTop: '4px',
    lineHeight: 1.45,
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    color: `${FIELD_DISABLED_HINT} !important`,
    WebkitTextFillColor: `${FIELD_DISABLED_HINT} !important`,
    opacity: '1 !important',
  },
  '& .MuiFormHelperText-root.Mui-error': { color: `${ROSE} !important`, WebkitTextFillColor: `${ROSE} !important` },
  '& .MuiOutlinedInput-root .MuiSelect-select': {
    color: `${alpha(TEXT_BODY, 0.9)} !important`,
    WebkitTextFillColor: `${alpha(TEXT_BODY, 0.9)} !important`,
  },
};

// ─── Reusable primitives ──────────────────────────────────────────────────────
const StyledField = ({ sx, helperText, multiline, rows, minRows, ...props }) => (
  <TextField
    {...props}
    multiline={multiline}
    rows={rows}
    minRows={minRows}
    helperText={multiline ? helperText : helperSlot(helperText)}
    sx={{ ...inputRootSx, ...sx }}
  />
);

/** Monospace tag pill — e.g. "PDF", "CURRENT", "PNG" */
const MonoTag = ({ label, color = NEON2 }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center',
    px: 0.75, py: '2px', borderRadius: '5px',
    background: alpha(color, 0.1),
    border: `1px solid ${alpha(color, 0.22)}`,
  }}>
    <Typography sx={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.58rem', fontWeight: 600,
      letterSpacing: 0.8, color,
    }}>
      {label}
    </Typography>
  </Box>
);

/** Small icon action button */
const ActionBtn = ({ label, onClick, color, children }) => (
  <Box
    title={label}
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 30, height: 30, borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
      color: color ?? alpha(TEXT_MUTED, 0.65),
      border: `1px solid ${alpha(color ?? NEON, 0.15)}`,
      background: 'transparent',
      transition: 'all 0.16s ease',
      '&:hover': {
        background: alpha(color ?? NEON, 0.1),
        color: color ?? NEON,
        borderColor: alpha(color ?? NEON, 0.38),
      },
    }}
  >
    {children}
  </Box>
);

/** Click-anywhere toggle row */
const ClickableToggleRow = ({ checked, onToggle, activeColor = NEON3, children, reserveHelperSpace = false }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Box
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        minHeight: 56,
        px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
        background: checked ? alpha(activeColor, 0.07) : alpha(SURFACE_UP, 0.4),
        border: `1px solid ${checked ? alpha(activeColor, 0.2) : alpha(NEON, 0.08)}`,
        transition: 'all 0.18s ease',
        '&:hover': {
          background: checked ? alpha(activeColor, 0.1) : alpha(SURFACE_UP, 0.55),
          borderColor: checked ? alpha(activeColor, 0.3) : alpha(NEON, 0.14),
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>{children}</Box>
      <Switch
        checked={checked}
        tabIndex={-1}
        size="small"
        sx={{
          pointerEvents: 'none', flexShrink: 0,
          '& .MuiSwitch-switchBase.Mui-checked': { color: activeColor },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: alpha(activeColor, 0.45) },
        }}
      />
    </Box>
    {reserveHelperSpace && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: 'transparent', mt: 0.5, px: 1.5 }}>
        {helperSlot(undefined)}
      </Typography>
    )}
  </Box>
);

/** Section card — wraps CommonSectionCard with consistent id forwarding */
const SectionCard = ({ title, icon, delay = 0, children, accentNeon, sectionId, ...rest }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay} accentNeon={accentNeon} id={sectionId} {...rest}>
    {children}
  </CommonSectionCard>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};
const isImageFile = (f) => f?.type?.startsWith('image/');
const isPdfFile   = (f) => f?.type === 'application/pdf';

// ─── Claim Tabs ───────────────────────────────────────────────────────────────
const STATUS_META = {
  approved: { color: NEON3,  Icon: ApproveIcon },
  rejected: { color: ROSE,   Icon: RejectIcon  },
  on_hold:  { color: AMBER,  Icon: WarningIcon  },
  pending:  { color: NEON2,  Icon: PendingIcon  },
};

const ClaimTabs = ({ activeTab, onTabChange, showMgmt, claimStatus }) => {
  const meta = STATUS_META[claimStatus] ?? STATUS_META.pending;
  const { color: statusColor, Icon: StatusIcon } = meta;

  const tabs = [
    { value: 0, label: 'Request', Icon: PersonIcon,    color: NEON  },
    ...(showMgmt ? [{ value: 1, label: 'Review',  Icon: TreasurerIcon, color: NEON2 }] : []),
  ];

  return (
    <Box sx={{
      mb: 2.5,
      background: alpha(SURFACE_CARD, 0.9),
      border: `1px solid ${alpha(NEON, 0.09)}`,
      borderRadius: '14px',
      overflow: 'hidden',
    }}>
      {/* Tab row */}
      <Box sx={{ display: 'flex' }}>
        {tabs.map(({ value, label, Icon, color }) => {
          const isActive = activeTab === value;
          return (
            <Box
              key={value}
              onClick={() => onTabChange(value)}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.75, py: 1.5, px: 1.5, cursor: 'pointer', position: 'relative',
                background: isActive ? alpha(color, 0.07) : 'transparent',
                borderBottom: `2px solid ${isActive ? color : 'transparent'}`,
                transition: 'all 0.2s ease',
                '&:hover': { background: alpha(color, 0.04) },
              }}
            >
              <Icon sx={{
                fontSize: 15,
                color: isActive ? color : alpha(TEXT_MUTED, 0.45),
                transition: 'color 0.2s',
              }} />
              <Typography sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700, fontSize: '0.82rem', letterSpacing: 0.6,
                color: isActive ? color : alpha(TEXT_MUTED, 0.5),
                transition: 'color 0.2s',
              }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Status badge strip */}
      {claimStatus && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          px: 2, py: 0.6,
          borderTop: `1px solid ${alpha(NEON, 0.06)}`,
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1.25, py: '3px', borderRadius: '6px',
            background: alpha(statusColor, 0.1),
            border: `1px solid ${alpha(statusColor, 0.22)}`,
          }}>
            <StatusIcon sx={{ fontSize: 11, color: statusColor }} />
            <Typography sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700, fontSize: '0.65rem',
              color: statusColor, letterSpacing: 1, textTransform: 'uppercase',
            }}>
              {claimStatus}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Receipt upload zone ──────────────────────────────────────────────────────
const ReceiptUploadZone = ({
  inputId, file, savedUrl, previewUrl, accentColor = NEON,
  uploadTitle, ocrEnabled,
  onFileChange, onDropFile, onRemove,
  validationError, isEditMode, savedFileName, savedIsPdf = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hasNewFile  = Boolean(file);
  const hasExisting = isEditMode && savedUrl && !hasNewFile;
  const hasAnyFile  = hasNewFile || hasExisting;
  const isPdf = file ? isPdfFile(file) : (hasExisting && savedIsPdf);
  const isImg = file ? isImageFile(file) : (hasExisting && !savedIsPdf);
  const fileTypeTag = isPdf
    ? 'PDF'
    : (isImg && file?.type)
      ? file.type.split('/')[1].toUpperCase()
      : (hasExisting ? 'IMAGE' : null);
  const FileTypeIcon = isPdf ? PdfIcon : isImg ? ImageIcon : UploadIcon;

  const dropHandlers = {
    onDragOver: (e) => { e.preventDefault(); setIsDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); setIsDragOver(false); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); onDropFile?.(e.dataTransfer?.files?.[0]); },
  };

  if (!hasAnyFile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          onClick={() => document.getElementById(inputId).click()}
          {...dropHandlers}
          sx={{
            p: 2.5, textAlign: 'center', borderRadius: '12px', cursor: 'pointer',
            border: `2px dashed ${validationError ? ROSE : isDragOver ? accentColor : alpha(accentColor, 0.22)}`,
            background: validationError
              ? alpha(ROSE, 0.04)
              : isDragOver
                ? alpha(accentColor, 0.06)
                : alpha(SURFACE_UP, 0.28),
            transition: 'all 0.2s ease',
            '&:hover': {
              background: alpha(accentColor, 0.04),
              borderColor: alpha(accentColor, 0.42),
            },
          }}
        >
          {/* Icon bubble */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 42, height: 42, borderRadius: '11px', mb: 1.25,
            background: alpha(accentColor, isDragOver ? 0.14 : 0.08),
            border: `1px solid ${alpha(accentColor, isDragOver ? 0.45 : 0.18)}`,
            transition: 'all 0.2s ease',
          }}>
            <UploadIcon sx={{ fontSize: 20, color: isDragOver ? accentColor : alpha(accentColor, 0.65) }} />
          </Box>

          <Typography sx={{
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
            fontSize: '0.9rem', mb: 0.3,
            color: isDragOver ? accentColor : alpha(TEXT_BODY, 0.85),
          }}>
            {isDragOver ? 'Drop to upload' : uploadTitle}
          </Typography>
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.72rem', color: alpha(TEXT_MUTED, 0.5),
          }}>
            PDF · JPG · PNG &nbsp;·&nbsp; 10 MB max
            {ocrEnabled && ' · auto-fills fields'}
          </Typography>
        </Box>

        {validationError && (
          <Typography sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.67rem', color: ROSE, letterSpacing: 0.2, pl: 0.25,
          }}>
            ⚠ {validationError}
          </Typography>
        )}
        <input accept=".pdf,.jpg,.jpeg,.png" type="file" id={inputId} hidden onChange={onFileChange} />
      </Box>
    );
  }

  if (hasNewFile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, animation: 'file-appear 0.28s ease both' }}>
        <Box sx={{
          borderRadius: '11px', overflow: 'hidden',
          border: `1px solid ${alpha(NEON3, 0.22)}`,
          background: alpha(SURFACE_CARD, 0.8),
        }}>
          {/* File row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5 }}>
            {/* Type icon */}
            <Box sx={{
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, borderRadius: '9px',
              background: isPdf ? alpha(ROSE, 0.12) : alpha(NEON, 0.1),
              border: `1px solid ${alpha(isPdf ? ROSE : NEON, 0.22)}`,
            }}>
              <FileTypeIcon sx={{ fontSize: 18, color: isPdf ? ROSE : NEON }} />
            </Box>

            {/* Name + meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.3 }}>
                <CheckCircleIcon sx={{ fontSize: 11, color: NEON3, flexShrink: 0 }} />
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                  fontSize: '0.82rem', color: TEXT_BODY,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {file.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6),
                }}>
                  {formatBytes(file.size)}
                </Typography>
                {fileTypeTag && <MonoTag label={fileTypeTag} color={isPdf ? ROSE : NEON} />}
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {(isPdf || isImg) && (
                <ActionBtn label={previewOpen ? 'Collapse' : 'Preview'} onClick={() => setPreviewOpen(v => !v)}>
                  {previewOpen
                    ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                    : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
                </ActionBtn>
              )}
              <ActionBtn label="Replace" onClick={() => document.getElementById(inputId).click()} color={NEON}>
                <ReplaceIcon sx={{ fontSize: 14 }} />
              </ActionBtn>
              <ActionBtn label="Remove" onClick={onRemove} color={ROSE}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </ActionBtn>
            </Box>
          </Box>

          {/* Preview panel */}
          {previewOpen && previewUrl && (
            <Box sx={{
              borderTop: `1px solid ${alpha(NEON3, 0.12)}`,
              background: alpha(SURFACE_MID, 0.6),
              height: { xs: 200, md: 280 },
            }}>
              {isPdf
                ? <iframe src={`${previewUrl}#toolbar=0`} style={{ width: '100%', height: '100%', border: 'none' }} title="Receipt preview" />
                : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 1.5 }}>
                    <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }} />
                  </Box>
                )}
            </Box>
          )}
        </Box>
        <input accept=".pdf,.jpg,.jpeg,.png" type="file" id={inputId} hidden onChange={onFileChange} />
      </Box>
    );
  }

  // Existing saved file
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{
        borderRadius: '11px', overflow: 'hidden',
        border: `1px solid ${alpha(NEON2, 0.18)}`,
        background: alpha(SURFACE_CARD, 0.8),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5 }}>
          <Box sx={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 38, height: 38, borderRadius: '9px',
            background: alpha(accentColor, 0.1),
            border: `1px solid ${alpha(accentColor, 0.2)}`,
          }}>
            <ReceiptIcon sx={{ fontSize: 18, color: accentColor }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
              fontSize: '0.82rem', color: TEXT_BODY, mb: 0.3,
            }}>
              {savedFileName || 'Saved receipt'}
            </Typography>
            <MonoTag label="CURRENT" color={NEON2} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {previewUrl && (
              <ActionBtn label={previewOpen ? 'Collapse' : 'Preview'} onClick={() => setPreviewOpen(v => !v)}>
                {previewOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              </ActionBtn>
            )}
            <ActionBtn label="Open in new tab" onClick={() => previewUrl && window.open(previewUrl, '_blank')} color={NEON}>
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </ActionBtn>
          </Box>
        </Box>

        {previewOpen && previewUrl && (
          <Box sx={{
            borderTop: `1px solid ${alpha(NEON2, 0.1)}`,
            background: alpha(SURFACE_MID, 0.55),
            height: { xs: 200, md: 280 },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: isPdf ? 0 : 1.5,
          }}>
            {isPdf ? (
              <iframe src={`${previewUrl}#toolbar=0`} style={{ width: '100%', height: '100%', border: 'none' }} title="Saved receipt preview" />
            ) : (
              <img src={previewUrl} alt="Saved receipt" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }} onError={(e) => { e.target.style.display = 'none'; }} />
            )}
          </Box>
        )}
      </Box>

      {/* Replace drop zone */}
      <Box
        onClick={() => document.getElementById(inputId).click()}
        {...dropHandlers}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          py: 1.25, borderRadius: '9px', cursor: 'pointer',
          border: `1.5px dashed ${isDragOver ? alpha(accentColor, 0.65) : alpha(accentColor, 0.16)}`,
          background: isDragOver ? alpha(accentColor, 0.05) : alpha(SURFACE_UP, 0.18),
          transition: 'all 0.18s ease',
          '&:hover': { background: alpha(accentColor, 0.04), borderColor: alpha(accentColor, 0.32) },
        }}
      >
        <ReplaceIcon sx={{ fontSize: 15, color: alpha(accentColor, isDragOver ? 0.85 : 0.4) }} />
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
          fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.55),
        }}>
          Replace with a new file
        </Typography>
      </Box>
      <input accept=".pdf,.jpg,.jpeg,.png" type="file" id={inputId} hidden onChange={onFileChange} />
    </Box>
  );
};

const OCR_UNAVAILABLE_MSG = 'OCR preview unavailable right now.';

// ─── OCR Banner ───────────────────────────────────────────────────────────────
const OcrBanner = ({ loading, message, isError = false, onClose }) => {
  if (!loading && !message) return null;
  const color = loading ? AMBER : isError ? ROSE : NEON3;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1.5, py: 1.1, borderRadius: '9px', mb: 0.5,
      background: alpha(color, isError ? 0.1 : 0.06),
      border: `1px solid ${alpha(color, isError ? 0.38 : 0.18)}`,
      animation: 'banner-drop 0.25s ease both',
    }}>
      {loading
        ? <AutoAwesomeIcon sx={{ fontSize: 13, color: AMBER, animation: 'ocr-pulse 1.4s ease infinite', flexShrink: 0 }} />
        : isError
          ? <ErrorOutlineIcon sx={{ fontSize: 13, color: ROSE, flexShrink: 0 }} />
          : <CheckCircleIcon sx={{ fontSize: 13, color: NEON3, flexShrink: 0 }} />}
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem',
        color: isError ? alpha(ROSE, 0.95) : alpha(color, 0.9),
        fontWeight: isError ? 600 : 400,
        flex: 1,
      }}>
        {loading ? 'Reading receipt…' : message}
      </Typography>
      {!loading && message && (
        <Box onClick={onClose} sx={{ cursor: 'pointer', color: alpha(TEXT_MUTED, 0.45), fontSize: 13, '&:hover': { color: TEXT_MUTED } }}>✕</Box>
      )}
    </Box>
  );
};

// ─── Line items editor ────────────────────────────────────────────────────────
const MANY_ITEMS_THRESHOLD = 5;

const LineItemsEditor = ({ lineItems, onChange, readOnly, fieldHasError, fieldHelper, onRecalcAmount }) => {
  const filledCount = (lineItems || []).filter((row) => String(row?.name || '').trim()).length;
  const useScroll = (lineItems || []).length > MANY_ITEMS_THRESHOLD;

  if (readOnly) {
    return (
      <ClaimLineItemsHierarchy
        items={lineItems}
        accent={NEON}
        accent2={NEON3}
        maxHeight={280}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {filledCount > 1 && (
        <ClaimLineItemsHierarchy
          items={lineItems}
          accent={NEON}
          accent2={NEON3}
          summaryOnly
        />
      )}

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...(useScroll ? {
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: alpha(NEON, 0.25), borderRadius: 4 },
        } : {}),
      }}>
        {lineItems.map((row, index) => (
          <Box
            key={`li-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 148px auto' },
              gap: 1,
              alignItems: 'flex-start',
              p: 1.25,
              borderRadius: '10px',
              background: alpha(SURFACE_UP, 0.38),
              border: `1px solid ${alpha(NEON, 0.08)}`,
              ml: 0.5,
              borderLeft: `2px solid ${alpha(NEON, index === 0 ? 0.35 : 0.12)}`,
            }}
          >
            <StyledField
              fullWidth
              required={index === 0}
              label={`Item ${index + 1}`}
              value={row.name}
              onChange={(e) => onChange(index, 'name', e.target.value)}
              disabled={readOnly}
              error={index === 0 && fieldHasError('line_items')}
              helperText={index === 0 ? fieldHelper('line_items') || fieldHelper('claim_title') : undefined}
              InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
            />
            <StyledField
              fullWidth
              label="Amount (RM)"
              type="number"
              value={row.amount}
              onChange={(e) => onChange(index, 'amount', e.target.value)}
              disabled={readOnly}
              InputProps={{ startAdornment: <InputAdornment position="start"><MoneyIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
            />
            {!readOnly && lineItems.length > 1 && (
              <IconButton
                type="button"
                aria-label="Remove item"
                onClick={() => onChange(index, '__remove')}
                sx={{
                  mt: { xs: 0, sm: 0.5 },
                  color: ROSE, border: `1px solid ${alpha(ROSE, 0.2)}`,
                  borderRadius: '9px', p: '6px',
                  '&:hover': { background: alpha(ROSE, 0.08) },
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      {!readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <Button
            type="button" size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => onChange(-1, '__add')}
            sx={{
              borderRadius: '8px', color: NEON, fontSize: '0.78rem',
              border: `1px solid ${alpha(NEON, 0.24)}`,
              textTransform: 'none',
              '&:hover': { background: alpha(NEON, 0.06), borderColor: alpha(NEON, 0.4) },
            }}
          >
            Add item
          </Button>
          {onRecalcAmount && (
            <Button
              type="button" size="small"
              onClick={onRecalcAmount}
              sx={{
                borderRadius: '8px', color: NEON3, fontSize: '0.78rem',
                border: `1px solid ${alpha(NEON3, 0.24)}`,
                textTransform: 'none',
                '&:hover': { background: alpha(NEON3, 0.06), borderColor: alpha(NEON3, 0.4) },
              }}
            >
              Use line total
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Info banner (read-only / on-hold notice) ─────────────────────────────────
const InfoBanner = ({ color = NEON2, children }) => (
  <Box sx={{
    px: 1.5, py: 1, borderRadius: '9px',
    background: alpha(color, 0.06),
    border: `1px solid ${alpha(color, 0.16)}`,
  }}>
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.75) }}>
      {children}
    </Typography>
  </Box>
);

// ─── Member "for" selector ────────────────────────────────────────────────────
const ForSelector = ({ value, onChange, readOnly }) => {
  const opts = [{ self: true, label: 'Myself' }, { self: false, label: 'Someone else' }];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
      {opts.map(({ self, label }) => {
        const selected = Boolean(value) === self;
        return (
          <Box
            key={label}
            role="button"
            tabIndex={readOnly ? -1 : 0}
            onClick={() => !readOnly && onChange(self)}
            onKeyDown={(e) => { if (!readOnly && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChange(self); } }}
            sx={{
              py: 1.1, px: 1.5, borderRadius: '10px',
              cursor: readOnly ? 'default' : 'pointer',
              background: selected ? alpha(NEON, 0.09) : alpha(SURFACE_UP, 0.35),
              border: `1.5px solid ${selected ? alpha(NEON, 0.38) : alpha(NEON, 0.1)}`,
              transition: 'all 0.18s ease',
              '&:hover': !readOnly ? {
                borderColor: alpha(NEON, selected ? 0.5 : 0.2),
                background: selected ? alpha(NEON, 0.1) : alpha(SURFACE_UP, 0.45),
              } : {},
            }}
          >
            <Typography sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700, fontSize: '0.88rem',
              color: selected ? NEON : alpha(TEXT_MUTED, 0.55),
              transition: 'color 0.18s',
            }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Step 1 — Member request tab ──────────────────────────────────────────────
const MemberRequestTab = ({
  readOnly = false,
  formData, handleChange, handleNativeChange, handleLineItemChange, recalcAmountFromLineItems,
  validationErrors, fieldHelper, fieldHasError,
  currentUser, teamMembers, companies, isEditMode,
  purchasePreviewUrl, savedPurchaseReceiptUrl, savedPurchaseIsPdf = false,
  ocrLoading, ocrMessage, ocrIsError, clearOcrBanner,
  purchaseDateOpen, setPurchaseDateOpen,
  handlePurchaseReceiptChange, processPurchaseReceiptFile, handleRemovePurchaseReceipt,
}) => (
  <Box className="claim-tab-panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
    {readOnly && (
      <InfoBanner color={NEON2}>
        Member submission — open <strong>Review</strong> to approve or pay.
      </InfoBanner>
    )}
    {!readOnly && formData._claimStatus === 'on_hold' && (
      <InfoBanner color={AMBER}>
        On hold — save after updating to send back for review.
      </InfoBanner>
    )}

    {/* ── Receipt ── */}
    <SectionCard title="Receipt" icon={<ReceiptIcon />} delay={0} sectionId="section-receipt">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!readOnly && (
          <OcrBanner loading={ocrLoading} message={ocrMessage} isError={ocrIsError} onClose={clearOcrBanner} />
        )}

        {readOnly ? (
          purchasePreviewUrl
            ? <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => window.open(purchasePreviewUrl, '_blank')} sx={{ color: NEON, alignSelf: 'flex-start', textTransform: 'none', fontFamily: "'DM Sans',sans-serif" }}>View receipt</Button>
            : <Typography sx={{ fontSize: '0.84rem', color: alpha(TEXT_MUTED, 0.6), fontStyle: 'italic' }}>No receipt on file</Typography>
        ) : (
          <ReceiptUploadZone
            inputId="purchase-receipt-input"
            file={formData.receipt_file}
            savedUrl={savedPurchaseReceiptUrl}
            previewUrl={purchasePreviewUrl}
            accentColor={NEON}
            uploadTitle="Drop receipt or browse"
            ocrEnabled
            onFileChange={handlePurchaseReceiptChange}
            onDropFile={processPurchaseReceiptFile}
            onRemove={handleRemovePurchaseReceipt}
            validationError={fieldHelper('receipt_file')}
            isEditMode={isEditMode}
            savedFileName="Saved purchase receipt"
            savedIsPdf={savedPurchaseIsPdf}
          />
        )}

        {/* Merchant row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'flex-start' }}>
          {(() => {
            const merchantLocked = Boolean(formData.company_id);
            const manualDisabled = readOnly || merchantLocked;
            const merchantError  = fieldHasError('company_name');
            const merchantHelper = helperSlot(merchantError ? fieldHelper('company_name') : undefined);
            return (
              <>
                <SearchableSelect
                  sx={inputRootSx}
                  required label="Merchant"
                  value={formData.company_id === '' ? '' : String(formData.company_id)}
                  onChange={(v) => handleChange('company_id', v === '' ? '' : v)}
                  options={[{ value: '', label: 'None' }, ...companies.map((c) => ({ value: String(c.id), label: c.name }))]}
                  startIcon={<BusinessIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} />}
                  disabled={readOnly}
                  error={merchantError}
                  helperText={merchantHelper}
                />
                <StyledField
                  fullWidth
                  required={!manualDisabled}
                  label="Merchant name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleNativeChange}
                  disabled={manualDisabled}
                  error={merchantError && !merchantLocked}
                  helperText={merchantHelper}
                  InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
                />
              </>
            );
          })()}
        </Box>

        {/* Date / payment / amount / receipt# */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <DatePicker
            label="Purchase date"
            value={formData.purchase_payment_date ? dayjs(formData.purchase_payment_date) : null}
            open={purchaseDateOpen}
            onOpen={() => !readOnly && setPurchaseDateOpen(true)}
            onClose={() => setPurchaseDateOpen(false)}
            disabled={readOnly}
            onChange={(v) => handleChange('purchase_payment_date', v?.isValid() ? v.format('YYYY-MM-DD') : '')}
            slotProps={{
              ...makePickerSlots('normal', <CalendarIcon />)(readOnly),
              textField: {
                ...makePickerSlots('normal', <CalendarIcon />)(readOnly).textField,
                required: true,
                error: fieldHasError('purchase_payment_date') || fieldHasError('payment_date'),
                helperText: helperSlot(fieldHelper('purchase_payment_date') || fieldHelper('payment_date')),
                onClick: () => !readOnly && setPurchaseDateOpen(true),
              },
              openPickerButton: { onClick: (e) => { e.stopPropagation(); if (!readOnly) setPurchaseDateOpen(true); } },
            }}
          />
          <SearchableSelect
            sx={inputRootSx}
            required label="How you paid"
            value={formData.purchase_payment_method}
            onChange={(v) => handleChange('purchase_payment_method', v)}
            options={MEMBER_PAYOUT_OPTIONS}
            startIcon={<PaymentIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} />}
            error={fieldHasError('purchase_payment_method')}
            helperText={helperSlot(fieldHelper('purchase_payment_method'))}
            disabled={readOnly}
          />
          <StyledField
            fullWidth required
            label="Amount (RM)" name="amount" type="number"
            value={formData.amount} onChange={handleNativeChange}
            disabled={readOnly}
            error={fieldHasError('amount')} helperText={helperSlot(fieldHelper('amount'))}
            InputProps={{ startAdornment: <InputAdornment position="start"><MoneyIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
          />
          <StyledField
            fullWidth required
            label="Receipt number" name="receipt_number"
            value={formData.receipt_number} onChange={handleNativeChange}
            disabled={readOnly}
            error={fieldHasError('receipt_number')} helperText={helperSlot(fieldHelper('receipt_number'))}
            InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
          />
        </Box>
      </Box>
    </SectionCard>

    {/* ── Claim details ── */}
    <SectionCard title="Claim details" icon={<DescriptionIcon />} delay={0.05} sectionId="section-usage">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <ForSelector
          value={formData.claim_for_self}
          onChange={(self) => handleChange('claim_for_self', self)}
          readOnly={readOnly}
        />
        {!formData.claim_for_self && (
          <SearchableSelect
            sx={inputRootSx}
            required label="Team member"
            value={formData.claimed_for_user_id === '' ? '' : String(formData.claimed_for_user_id)}
            onChange={(v) => handleChange('claimed_for_user_id', v === '' ? '' : v)}
            options={[{ value: '', label: 'Select member…' }, ...teamMembers.map((u) => ({ value: String(u.id), label: u.name }))]}
            startIcon={<PersonIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} />}
            disabled={readOnly}
            error={fieldHasError('claimed_for_user_id')}
            helperText={helperSlot(fieldHelper('claimed_for_user_id'))}
          />
        )}
        <LineItemsEditor
          lineItems={formData.line_items}
          onChange={handleLineItemChange}
          readOnly={readOnly}
          fieldHasError={fieldHasError}
          fieldHelper={fieldHelper}
          onRecalcAmount={recalcAmountFromLineItems}
        />
        <StyledField
          fullWidth required multiline rows={2}
          label="What was it for?" name="description"
          value={formData.description} onChange={handleNativeChange}
          disabled={readOnly}
          error={fieldHasError('description')} helperText={fieldHelper('description')}
          InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
        />
      </Box>
    </SectionCard>

    {/* ── Reimbursement ── */}
    <SectionCard title="Reimbursement" icon={<PaymentIcon />} delay={0.09} sectionId="section-payout">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          <SearchableSelect
            sx={inputRootSx}
            required label="Payout method"
            value={formData.member_payout_method}
            onChange={(v) => handleChange('member_payout_method', v)}
            options={MEMBER_PAYOUT_OPTIONS}
            startIcon={<PaymentIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} />}
            error={fieldHasError('member_payout_method')}
            helperText={helperSlot(fieldHelper('member_payout_method'))}
            disabled={readOnly}
          />
          <StyledField
            fullWidth required
            label={formData.claim_for_self ? 'Your account / number' : 'Their account / number'}
            name="payment_account"
            value={formData.payment_account} onChange={handleNativeChange}
            disabled={readOnly}
            error={fieldHasError('payment_account')} helperText={helperSlot(fieldHelper('payment_account'))}
            InputProps={{ startAdornment: <InputAdornment position="start"><AccountBalanceIcon sx={{ color: alpha(NEON, 0.38), fontSize: 17 }} /></InputAdornment> }}
          />
        </Box>
        {!readOnly && (
          <ClickableToggleRow
            checked={Boolean(formData.save_payout_preferences)}
            onToggle={() => handleChange('save_payout_preferences', !formData.save_payout_preferences)}
            activeColor={NEON2}
          >
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.85rem', fontWeight: 500, color: TEXT_BODY }}>
              Save payout details for next time
            </Typography>
          </ClickableToggleRow>
        )}
      </Box>
    </SectionCard>
  </Box>
);

// ─── Step 2 — Review tab ──────────────────────────────────────────────────────
const REVIEW_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending', Icon: PendingIcon, color: NEON2 },
  { label: 'Approve', value: 'approved', Icon: ApproveIcon, color: NEON3 },
  { label: 'On hold', value: 'on_hold',  Icon: WarningIcon, color: AMBER },
  { label: 'Reject',  value: 'rejected', Icon: RejectIcon,  color: ROSE  },
];

const ReviewProcessTab = ({
  formData, handleChange, validationErrors, setValidationErrors,
  fieldHelper, fieldHasError,
  budgets, budgetsLoadWarning,
  purchasePreviewUrl,
  reimbursementPreviewUrl, existingReimbursementUrl, savedReimbursementIsPdf = false,
  handleReimbursementReceiptChange, processReimbursementReceiptFile, handleRemoveReimbursementReceipt,
  isEditMode, id, currentUser, companies, teamMembers,
}) => {
  const copyResetRef = useRef(null);
  const [copiedField, setCopiedField] = useState('');
  const isApproved = formData.status === 'approved';
  const isRejected = formData.status === 'rejected';
  const isPaid     = Boolean(formData.is_paid);
  const paidViaStripe = formData.club_payment_method === 'stripe';

  const merchantLabel = formData.company_name
    || companies.find((c) => String(c.id) === String(formData.company_id))?.name
    || '—';
  const summaryItems = buildClaimTitleFromLineItems(formData.line_items, formData.claim_title) || '—';
  const claimForLabel = formData.claim_for_self
    ? (currentUser?.name || 'Self')
    : (teamMembers.find((u) => String(u.id) === String(formData.claimed_for_user_id))?.name || '—');
  const amountLabel = formData.amount ? `RM ${Number(formData.amount).toLocaleString()}` : '—';
  const preferredMethodLabel = formData.member_payout_method
    ? String(formData.member_payout_method).replace(/_/g, ' ')
    : '—';
  const preferredAccountLabel = formData.payment_account || '—';

  useEffect(() => () => {
    if (copyResetRef.current) clearTimeout(copyResetRef.current);
  }, []);

  const copyText = async (text, key) => {
    const payload = String(text || '').trim();
    if (!payload || payload === '—') return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const t = document.createElement('textarea');
        t.value = payload;
        t.setAttribute('readonly', '');
        t.style.position = 'absolute';
        t.style.left = '-9999px';
        document.body.appendChild(t);
        t.select();
        document.execCommand('copy');
        document.body.removeChild(t);
      }
      setCopiedField(key);
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopiedField(''), 1200);
    } catch {
      // no-op: copy not available in current browser context
    }
  };

  /** Dim bullet separator */
  const Sep = () => (
    <Typography component="span" sx={{ color: alpha(NEON2, 0.3), mx: 0.5, userSelect: 'none' }}>·</Typography>
  );

  return (
    <Box className="claim-tab-panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SectionCard title="Review" icon={<GavelIcon />} delay={0} accentNeon={NEON2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Claim summary strip */}
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25,
            px: 1.5, py: 1, borderRadius: '9px',
            background: alpha(SURFACE_UP, 0.38),
            border: `1px solid ${alpha(NEON2, 0.1)}`,
          }}>
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', color: TEXT_BODY }}>
              <Box component="span" sx={{ color: alpha(TEXT_MUTED, 0.6), mr: 0.4 }}>Items:</Box>
              {summaryItems}
            </Typography>
            <Sep />
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', color: TEXT_BODY }}>
              <Box component="span" sx={{ color: alpha(TEXT_MUTED, 0.6), mr: 0.4 }}>For:</Box>
              {claimForLabel}
            </Typography>
            <Sep />
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', fontWeight: 600, color: NEON2 }}>
              {amountLabel}
            </Typography>
            <Sep />
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', color: TEXT_BODY }}>
              <Box component="span" sx={{ color: alpha(TEXT_MUTED, 0.6), mr: 0.4 }}>Merchant:</Box>
              {merchantLabel}
            </Typography>
            {formData.receipt_number && (
              <>
                <Sep />
                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.76rem', color: alpha(TEXT_MUTED, 0.75) }}>
                  #{formData.receipt_number}
                </Typography>
              </>
            )}
            {purchasePreviewUrl && (
              <Button
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                onClick={() => window.open(purchasePreviewUrl, '_blank')}
                sx={{ ml: 'auto', color: NEON2, minWidth: 0, py: 0.2, textTransform: 'none', fontFamily: "'DM Sans',sans-serif", fontSize: '0.78rem' }}
              >
                Receipt
              </Button>
            )}
          </Box>

          {/* Creator payout preference */}
          <Box sx={{
            px: 1.5, py: 1.2, borderRadius: '9px',
            background: alpha(SURFACE_UP, 0.32),
            border: `1px solid ${alpha(NEON2, 0.1)}`,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.25,
          }}>
            {[
              { key: 'method', label: 'Creator preferred method', value: preferredMethodLabel },
              { key: 'account', label: 'Creator account / number', value: preferredAccountLabel },
            ].map(({ key, label, value }) => (
              <Box key={key} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.64rem', color: alpha(TEXT_MUTED, 0.62), letterSpacing: 0.4, mb: 0.45 }}>
                  {label.toUpperCase()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.84rem', color: TEXT_BODY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {value}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyText(value, key)}
                    sx={{
                      color: copiedField === key ? NEON3 : alpha(NEON2, 0.7),
                      border: `1px solid ${alpha(copiedField === key ? NEON3 : NEON2, 0.28)}`,
                      borderRadius: '8px',
                      p: '4px',
                    }}
                    title="Copy"
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Status selector */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
            {REVIEW_STATUS_OPTIONS.map(({ label, value, Icon, color }) => {
              const isSelected = formData.status === value;
              return (
                <Box
                  key={value}
                  onClick={() => handleChange('status', value)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    py: 1.25, borderRadius: '10px', cursor: 'pointer',
                    background: isSelected ? alpha(color, 0.1) : alpha(SURFACE_UP, 0.35),
                    border: `1.5px solid ${isSelected ? alpha(color, 0.38) : alpha(color, 0.12)}`,
                    transition: 'all 0.18s ease',
                    '&:hover': { background: alpha(color, 0.07), borderColor: alpha(color, 0.26) },
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: isSelected ? color : alpha(color, 0.38) }} />
                  <Typography sx={{
                    fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: '0.86rem',
                    color: isSelected ? color : alpha(color, 0.42),
                  }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Rejection reason */}
          {isRejected && (
            <StyledField
              fullWidth multiline minRows={2} required
              label="Rejection reason"
              name="rejection_reason"
              value={formData.rejection_reason}
              onChange={(e) => {
                handleChange('rejection_reason', e.target.value);
                if (fieldHasError('rejection_reason')) setValidationErrors((p) => ({ ...p, rejection_reason: '' }));
              }}
              error={fieldHasError('rejection_reason')}
              helperText={fieldHelper('rejection_reason')}
              InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(ROSE, 0.4), fontSize: 17 }} /></InputAdornment> }}
            />
          )}

          {/* Paid toggle + payment method + upload */}
          {isApproved && (
            <>
              <ClickableToggleRow
                checked={isPaid}
                onToggle={() => handleChange('is_paid', !isPaid)}
                activeColor={NEON3}
              >
                <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.86rem', fontWeight: 500, color: TEXT_BODY }}>
                  Paid from club account
                </Typography>
              </ClickableToggleRow>

              {isPaid && (
                <>
                  <SearchableSelect
                    sx={inputRootSx}
                    required label="Club payment method"
                    value={formData.club_payment_method}
                    onChange={(v) => {
                      handleChange('club_payment_method', v);
                      if (fieldHasError('club_payment_method')) setValidationErrors((p) => ({ ...p, club_payment_method: '' }));
                    }}
                    options={CLUB_PAYOUT_OPTIONS}
                    startIcon={<PaymentIcon sx={{ color: alpha(NEON3, 0.4), fontSize: 17 }} />}
                    error={fieldHasError('club_payment_method')}
                    helperText={helperSlot(fieldHelper('club_payment_method'))}
                  />
                  {!paidViaStripe && (
                    <ReceiptUploadZone
                      inputId="reimbursement-receipt-input"
                      file={formData.reimbursement_receipt_file}
                      savedUrl={existingReimbursementUrl}
                      previewUrl={reimbursementPreviewUrl}
                      accentColor={NEON3}
                      uploadTitle="Upload club payment proof"
                      onFileChange={handleReimbursementReceiptChange}
                      onDropFile={processReimbursementReceiptFile}
                      onRemove={handleRemoveReimbursementReceipt}
                      validationError={fieldHelper('reimbursement_receipt')}
                      isEditMode={isEditMode}
                      savedFileName="Saved club payment proof"
                      savedIsPdf={savedReimbursementIsPdf}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* Budget + claimable */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'stretch' }}>
            <SearchableSelect
              sx={inputRootSx}
              label="Charge to budget"
              value={formData.budget_id === '' ? '' : String(formData.budget_id)}
              onChange={(v) => handleChange('budget_id', v === '' ? '' : v)}
              options={[{ value: '', label: 'None' }, ...budgets.map((b) => ({ value: String(b.id), label: formatBudgetOptionLabel(b) }))]}
              startIcon={<AccountBalanceIcon sx={{ color: alpha(NEON2, 0.4), fontSize: 17 }} />}
              helperText={helperSlot(budgetsLoadWarning || fieldHelper('budget_id'))}
              error={fieldHasError('budget_id')}
            />
            <ClickableToggleRow
              checked={formData.is_claimable}
              onToggle={() => handleChange('is_claimable', !formData.is_claimable)}
              activeColor={NEON3}
              reserveHelperSpace
            >
              <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.86rem', fontWeight: 500, color: TEXT_BODY }}>
                Claimable from university
              </Typography>
            </ClickableToggleRow>
          </Box>
        </Box>
      </SectionCard>
    </Box>
  );
};

// ─── Main ClaimForm ────────────────────────────────────────────────────────────
const ClaimForm = () => {
  const { id }     = useParams();
  const isEditMode = Boolean(id);
  const navigate   = useNavigate();
  const { user: currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => { if (activeTab > 1) setActiveTab(1); }, [activeTab]);

  const [formData, setFormData] = useState({
    claim_title: '',
    line_items: [emptyLineItem()],
    claim_for_self: true,
    claimed_for_user_id: '',
    description: '', amount: '',
    budget_id: '',
    company_id: '', company_name: '',
    purchase_payment_method: 'bank_transfer',
    purchase_payment_date: '',
    receipt_number: '',
    receipt_file: null,
    payment_account: '',
    member_payout_method: 'bank_transfer',
    save_payout_preferences: true,
    club_payment_method: 'bank_transfer',
    reimbursement_receipt_file: null,
    status: 'pending',
    is_paid: false,
    is_claimable: false,
    rejection_reason: '',
  });

  const [teamMembers,               setTeamMembers]               = useState([]);
  const [purchasePreviewUrl,         setPurchasePreviewUrl]        = useState(null);
  const [reimbursementPreviewUrl,    setReimbursementPreviewUrl]   = useState(null);
  const [existingReimbursementUrl,   setExistingReimbursementUrl]  = useState(null);
  const [savedPurchaseReceiptUrl,    setSavedPurchaseReceiptUrl]   = useState(null);
  const [savedPurchaseIsPdf,         setSavedPurchaseIsPdf]        = useState(false);
  const [savedReimbursementIsPdf,      setSavedReimbursementIsPdf]     = useState(false);
  const [companies,                  setCompanies]                 = useState([]);
  const [budgets,                    setBudgets]                   = useState([]);
  const [budgetsLoadWarning,         setBudgetsLoadWarning]        = useState(null);
  const [companiesLoadWarning,       setCompaniesLoadWarning]      = useState(null);
  const [loading,                    setLoading]                   = useState(false);
  const [dataLoading,                setDataLoading]               = useState(false);
  const [error,                      setError]                     = useState(null);
  const [successMessage,             setSuccessMessage]            = useState('');
  const [validationErrors,           setValidationErrors]          = useState({});
  const [showFieldErrors,            setShowFieldErrors]           = useState(false);
  const [confirmUpdateDialogOpen,    setConfirmUpdateDialogOpen]   = useState(false);
  const [pendingUpdateRequest,       setPendingUpdateRequest]      = useState(null);
  const [claimOwnerId,               setClaimOwnerId]              = useState(null);
  const [claimStatus,                setClaimStatus]               = useState(null);
  const [ocrLoading,                 setOcrLoading]                = useState(false);
  const [ocrMessage,                 setOcrMessage]                = useState('');
  const [ocrIsError,                 setOcrIsError]                = useState(false);
  const clearOcrBanner = () => { setOcrMessage(''); setOcrIsError(false); };
  const [purchaseDateOpen,           setPurchaseDateOpen]          = useState(false);
  const [reviewDraftReady,           setReviewDraftReady]          = useState(false);
  const reviewBaselineRef = useRef(null);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'claim', userId: currentUser?.id,
    isEditMode, enabled: !dataLoading && !isEditMode,
    formData, setFormData,
  });

  const { formDraft: reviewFormDraft, submitDraftProps: reviewSubmitDraftProps } = useFormDraftBindings({
    formKey: id ? `claim-review-${id}` : 'claim-review',
    userId: currentUser?.id,
    isEditMode: true, allowEditDraft: true,
    enabled: !dataLoading && isEditMode && Boolean(id) && reviewDraftReady,
    formData, setFormData,
    pickSnapshot: pickReviewDraftSlice,
    mergeSnapshot: (prev, draft) => ({ ...prev, ...pickReviewDraftSlice(draft) }),
    onDiscardDraft: () => {
      if (reviewBaselineRef.current) {
        setFormData((prev) => ({ ...prev, ...reviewBaselineRef.current }));
      }
    },
  });

  useEffect(() => {
    const cssId = 'claimform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS + PICKER_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    setClaimOwnerId(null); setClaimStatus(null); setReviewDraftReady(false);
    reviewBaselineRef.current = null;
  }, [id]);

  useEffect(() => {
    const load = async () => {
      setDataLoading(Boolean(isEditMode && id));
      try {
        try {
          const ur = await api.get('/users', { params: { minimal: true, active_only: true } });
          setTeamMembers(Array.isArray(ur.data) ? ur.data : (ur.data?.data || []));
        } catch { setTeamMembers([]); }

        if (!isEditMode) {
          try {
            const pr = await api.get('/claims/payout-preferences');
            const method = pr.data?.payment_method;
            const account = pr.data?.payment_account;
            if (method || account) {
              setFormData((prev) => ({
                ...prev,
                ...(method ? { member_payout_method: method } : {}),
                ...(account ? { payment_account: account } : {}),
              }));
            }
          } catch { /* no saved prefs */ }
        }

        try {
          const br = await api.get('/budgets', { params: { per_page: 100 } });
          setBudgets(Array.isArray(br.data?.data ?? br.data) ? (br.data?.data ?? br.data) : []);
          setBudgetsLoadWarning(null);
        } catch { setBudgets([]); setBudgetsLoadWarning('Budget list could not be loaded.'); }

        let list = [];
        try {
          const cr = await api.get('/claims/company-options');
          list = Array.isArray(cr.data) ? cr.data : (cr.data?.data || []);
          setCompanies(list); setCompaniesLoadWarning(null);
        } catch {
          try {
            const cr2 = await api.get('/companies/for-claim-form');
            list = Array.isArray(cr2.data) ? cr2.data : (cr2.data?.data || []);
            setCompanies(list); setCompaniesLoadWarning(null);
          } catch { setCompanies([]); setCompaniesLoadWarning('Company directory could not be loaded.'); }
        }

        if (!isEditMode || !id) return;

        try {
          const r = await api.get(`/claims/${id}`);
          const d = r.data?.data || r.data;
          let companyId = d.company_id != null && d.company_id !== '' ? String(d.company_id) : '';
          let companyNameManual = '';
          if (!companyId && (d.company_name || d.company)) {
            const name = (d.company_name || d.company || '').trim();
            const match = list.find((c) => c.name === name);
            if (match) companyId = String(match.id); else if (name) companyNameManual = name;
          }
          const payDateStr = (d.purchase_payment_date || d.payment_date) ? String(d.purchase_payment_date || d.payment_date).slice(0, 10) : '';
          const paid = Boolean(d.is_paid || d.status === 'paid');
          const loadedStatus = d.status === 'paid' ? 'approved' : (d.status || 'pending');
          setClaimStatus(loadedStatus);
          const storedPayMethod = d.payment_method || d.reimbursement_payment_method || 'bank_transfer';
          const lineItems = normalizeLineItemsFromApi(d.line_items, d.claim_title || d.item_bought);
          setFormData({
            claim_title: d.claim_title || d.item_bought || '',
            line_items: lineItems,
            claim_for_self: !d.claimed_for_user_id,
            claimed_for_user_id: d.claimed_for_user_id != null ? String(d.claimed_for_user_id) : '',
            description: d.description || '',
            amount: d.amount ?? '', budget_id: d.budget_id != null && d.budget_id !== '' ? String(d.budget_id) : '',
            company_id: companyId, company_name: companyNameManual,
            purchase_payment_method: d.purchase_payment_method || 'bank_transfer',
            purchase_payment_date: payDateStr, receipt_file: null, receipt_number: d.receipt_number ?? '',
            payment_account: d.payment_account ?? '',
            member_payout_method: paid ? 'bank_transfer' : storedPayMethod,
            club_payment_method: paid ? storedPayMethod : 'bank_transfer',
            reimbursement_receipt_file: null,
            status: loadedStatus, _claimStatus: loadedStatus,
            is_paid: Boolean(d.is_paid || d.status === 'paid'),
            is_claimable: Boolean(d.is_claimable),
            rejection_reason: d.rejection_reason ?? '',
            save_payout_preferences: false,
          });
          reviewBaselineRef.current = pickReviewDraftSlice({
            status: loadedStatus,
            rejection_reason: d.rejection_reason ?? '',
            is_paid: Boolean(d.is_paid || d.status === 'paid'),
            club_payment_method: paid ? storedPayMethod : 'bank_transfer',
            budget_id: d.budget_id != null && d.budget_id !== '' ? String(d.budget_id) : '',
            is_claimable: Boolean(d.is_claimable),
          });
          setSavedPurchaseIsPdf(receiptPathIsPdf(d, 'purchase'));
          if (signedPreviewUrlIsUsable(d.purchase_receipt_preview_url)) {
            setSavedPurchaseReceiptUrl(d.purchase_receipt_preview_url);
            setPurchasePreviewUrl(d.purchase_receipt_preview_url);
          } else if (claimHasSavedPurchaseReceipt(d)) {
            fetchClaimReceiptPreviewBlob(id, 'purchase')
              .then((preview) => {
                setSavedPurchaseReceiptUrl(preview.url);
                setPurchasePreviewUrl(preview.url);
                setSavedPurchaseIsPdf(preview.isPdf || receiptPathIsPdf(d, 'purchase'));
              })
              .catch(() => {
                setSavedPurchaseReceiptUrl(null);
                setPurchasePreviewUrl(null);
                setSavedPurchaseIsPdf(false);
              });
          } else {
            setSavedPurchaseReceiptUrl(null);
            setPurchasePreviewUrl(null);
            setSavedPurchaseIsPdf(false);
          }

          setSavedReimbursementIsPdf(receiptPathIsPdf(d, 'reimbursement'));
          if (signedPreviewUrlIsUsable(d.reimbursement_receipt_preview_url)) {
            setReimbursementPreviewUrl(d.reimbursement_receipt_preview_url);
            setExistingReimbursementUrl(d.reimbursement_receipt_preview_url);
          } else if (claimHasSavedReimbursementReceipt(d)) {
            fetchClaimReceiptPreviewBlob(id, 'reimbursement')
              .then((preview) => {
                setReimbursementPreviewUrl(preview.url);
                setExistingReimbursementUrl(preview.url);
                setSavedReimbursementIsPdf(preview.isPdf || receiptPathIsPdf(d, 'reimbursement'));
              })
              .catch(() => {
                setReimbursementPreviewUrl(null);
                setExistingReimbursementUrl(null);
                setSavedReimbursementIsPdf(false);
              });
          } else {
            setReimbursementPreviewUrl(null);
            setExistingReimbursementUrl(null);
            setSavedReimbursementIsPdf(false);
          }
          setClaimOwnerId(d.user_id != null ? d.user_id : null);
          setError(null); setReviewDraftReady(true);
        } catch { setError('Failed to fetch claim data.'); }
      } finally { setDataLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'company_id' && value) next.company_name = '';
      if (field === 'company_name' && String(value).trim() !== '') next.company_id = '';
      if (field === 'claim_for_self' && value) next.claimed_for_user_id = '';
      if (field === 'status' && (value === 'rejected' || value === 'on_hold')) next.is_paid = false;
      if (field === 'is_paid' && value) next.status = next.status === 'rejected' ? 'approved' : next.status;
      return next;
    });
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev, [field]: '' };
        if (field === 'company_id') delete next.company_name;
        if (field === 'company_name') delete next.company_name;
        if (field === 'purchase_payment_date') delete next.payment_date;
        return next;
      });
    }
  };

  const handleNativeChange = (e) => handleChange(e.target.name, e.target.value);

  const handleLineItemChange = (index, field, value) => {
    setFormData((prev) => {
      const items = [...(prev.line_items || [])];
      if (field === '__add') return { ...prev, line_items: [...items, emptyLineItem()] };
      if (field === '__remove') {
        const next = items.filter((_, i) => i !== index);
        return { ...prev, line_items: next.length ? next : [emptyLineItem()] };
      }
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
    if (showFieldErrors && (validationErrors.line_items || validationErrors.claim_title)) {
      setValidationErrors((p) => { const next = { ...p }; delete next.line_items; delete next.claim_title; return next; });
    }
  };

  const recalcAmountFromLineItems = () => {
    const sum = sumLineItemAmounts(formData.line_items);
    if (sum != null) handleChange('amount', sum);
  };

  const validateReceiptFile = (file, label) => {
    if (!file) return false;
    if (file.size > 10 * 1024 * 1024) { setError(`${label} must be less than 10MB`); return false; }
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) { setError('Please select a valid PDF or image file'); return false; }
    return true;
  };

  const processPurchaseReceiptFile = (file) => {
    if (!validateReceiptFile(file, 'Receipt')) return;
    setFormData((prev) => ({ ...prev, receipt_file: file }));
    setPurchasePreviewUrl(URL.createObjectURL(file));
    runReceiptOcr(file);
    setError(null);
    if (showFieldErrors && validationErrors.receipt_file) {
      setValidationErrors((prev) => ({ ...prev, receipt_file: '' }));
    }
  };

  const runReceiptOcr = async (file) => {
    if (!(file instanceof File)) return;
    try {
      setOcrLoading(true);
      clearOcrBanner();
      const fd = new FormData();
      fd.append('receipt_file', file);
      const response = await api.post('/claims/ocr-preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const fields = response?.data?.fields || {};
      const normAmount = (v) => v == null ? '' : String(v).replace(/[^0-9.]/g, '');
      const normDate   = (v) => {
        if (v == null) return '';
        const raw = String(v).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
        const dmy = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
        if (dmy) {
          const dd = dmy[1].padStart(2, '0');
          const mm = dmy[2].padStart(2, '0');
          const yy = dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3];
          return `${yy}-${mm}-${dd}`;
        }
        const p = dayjs(raw);
        return p.isValid() ? p.format('YYYY-MM-DD') : '';
      };
      setFormData((prev) => {
        const next = { ...prev };
        const fillIfEmpty = (key, incoming) => {
          const candidate = incoming == null ? '' : String(incoming).trim();
          if (!candidate) return;
          if (!(prev[key] == null ? '' : String(prev[key]).trim())) next[key] = candidate;
        };
        const ocrLineItems = Array.isArray(fields.line_items)
          ? fields.line_items
              .map((row) => ({
                name: String(row?.name ?? row?.item ?? '').trim(),
                amount: row?.amount != null && row.amount !== '' ? normAmount(row.amount) : '',
              }))
              .filter((row) => row.name || row.amount)
          : [];
        if (ocrLineItems.length > 0) next.line_items = ocrLineItems;
        const lineSum = sumLineItemAmounts(ocrLineItems);
        const ocrTotal = normAmount(fields.amount);
        if (lineSum) next.amount = lineSum;
        else if (ocrTotal && !String(prev.amount || '').trim()) next.amount = ocrTotal;
        else fillIfEmpty('amount', fields.amount);
        fillIfEmpty('claim_title', fields.claim_title);
        fillIfEmpty('receipt_number', fields.receipt_number);
        fillIfEmpty('purchase_payment_date', normDate(fields.purchase_payment_date));
        if (!prev.company_id) fillIfEmpty('company_name', fields.company_name);
        return next;
      });
      const parts = [];
      if (fields.line_items?.length) parts.push(`${fields.line_items.length} item(s)`);
      if (fields.amount) parts.push('total');
      setOcrIsError(false);
      setOcrMessage(
        parts.length > 0
          ? `Filled from receipt: ${parts.join(', ')}. Check and edit if needed.`
          : 'Could not read items or total — enter them manually.',
      );
    } catch (err) {
      console.error('Receipt OCR preview failed:', err?.response?.data || err);
      setOcrIsError(true);
      setOcrMessage(OCR_UNAVAILABLE_MSG);
    } finally { setOcrLoading(false); }
  };

  const handlePurchaseReceiptChange     = (e) => processPurchaseReceiptFile(e.target.files[0]);
  const handleRemovePurchaseReceipt     = () => { setFormData((p) => ({ ...p, receipt_file: null })); setPurchasePreviewUrl(savedPurchaseReceiptUrl); };
  const processReimbursementReceiptFile = (file) => { if (!validateReceiptFile(file, 'Reimbursement receipt')) return; setFormData((p) => ({ ...p, reimbursement_receipt_file: file })); setReimbursementPreviewUrl(URL.createObjectURL(file)); setError(null); };
  const handleReimbursementReceiptChange = (e) => processReimbursementReceiptFile(e.target.files[0]);
  const handleRemoveReimbursementReceipt = () => { setFormData((p) => ({ ...p, reimbursement_receipt_file: null })); setReimbursementPreviewUrl(existingReimbursementUrl); };

  const buildClaimFormData = (src, { forUpdate } = {}) => {
    const fd = new FormData();
    const showMgmt = canManageAllClaims(currentUser);
    const isPaid = showMgmt && Boolean(src.is_paid);
    const appendScalar = (key, val) => {
      if (val === '' || val == null) return;
      if (typeof val === 'boolean') { fd.append(key, val ? '1' : '0'); return; }
      fd.append(key, val);
    };
    const title = buildClaimTitleFromLineItems(src.line_items, src.claim_title);
    appendScalar('claim_title', title);
    appendScalar('description', src.description);
    appendScalar('amount', src.amount);
    fd.append('claim_for_self', src.claim_for_self ? '1' : '0');
    if (!src.claim_for_self && src.claimed_for_user_id) fd.append('claimed_for_user_id', String(Number(src.claimed_for_user_id)));
    const filledItems = (src.line_items || [])
      .map((row) => ({ name: String(row?.name || '').trim(), amount: row?.amount === '' || row?.amount == null ? null : parseFloat(String(row.amount).replace(/[^0-9.]/g, '')) }))
      .filter((row) => row.name);
    if (filledItems.length) fd.append('line_items', JSON.stringify(filledItems));
    if (src.save_payout_preferences) fd.append('save_payout_preferences', '1');
    if (src.company_id) fd.append('company_id', String(Number(src.company_id)));
    else { const nm = (src.company_name || '').trim(); if (nm) appendScalar('company_name', nm); }
    appendScalar('purchase_payment_method', src.purchase_payment_method);
    appendScalar('purchase_payment_date', src.purchase_payment_date);
    appendScalar('receipt_number', src.receipt_number);
    appendScalar('payment_account', src.payment_account);
    appendScalar('payment_method', src.member_payout_method);
    if (showMgmt) {
      appendScalar('status', src.status);
      fd.append('is_claimable', src.is_claimable ? '1' : '0');
      fd.append('is_paid', isPaid ? '1' : '0');
      const rr = String(src.rejection_reason || '').trim(); if (rr) appendScalar('rejection_reason', rr);
      if (src.budget_id) fd.append('budget_id', String(Number(src.budget_id)));
      else if (forUpdate) fd.append('budget_id', '');
      if (isPaid) appendScalar('payment_method', src.club_payment_method);
    }
    if (src.receipt_file instanceof File) fd.append('receipt_file', src.receipt_file);
    if (showMgmt && src.reimbursement_receipt_file instanceof File) fd.append('reimbursement_receipt', src.reimbursement_receipt_file);
    if (forUpdate) fd.append('_method', 'PUT');
    return fd;
  };

  const MEMBER_VALIDATION_FIELDS = new Set([
    'claim_title', 'line_items', 'claimed_for_user_id', 'description', 'purchase_payment_date', 'amount',
    'purchase_payment_method', 'payment_account', 'member_payout_method',
    'receipt_number', 'company_name', 'receipt_file',
  ]);

  const collectMemberErrors = (fd) => {
    const errors = {};
    if (!(fd.line_items || []).some((row) => String(row?.name || '').trim()) && !fd.claim_title?.trim()) errors.line_items = 'Add at least one purchased item';
    if (!fd.claim_for_self && !fd.claimed_for_user_id) errors.claimed_for_user_id = 'Select who this claim is for';
    if (!fd.description?.trim()) errors.description = 'Usage / description is required';
    if (!fd.purchase_payment_date) errors.purchase_payment_date = 'Purchase date is required';
    if (!fd.amount) errors.amount = 'Amount is required';
    else if (Number.isNaN(Number(fd.amount)) || parseFloat(fd.amount) <= 0) errors.amount = 'Must be a positive number';
    if (!(fd.purchase_payment_method || '').trim()) errors.purchase_payment_method = 'How you paid is required';
    if (!(fd.payment_account || '').trim()) errors.payment_account = 'Your payout account is required';
    if (!(fd.member_payout_method || '').trim()) errors.member_payout_method = 'Preferred payout method is required';
    if (!(fd.receipt_number || '').trim()) errors.receipt_number = 'Receipt number is required';
    if (!fd.company_id && !(fd.company_name || '').trim()) errors.company_name = 'Select a merchant or enter a name';
    if (!purchasePreviewUrl && !(fd.receipt_file instanceof File)) errors.receipt_file = 'Purchase receipt is required';
    return errors;
  };

  const collectReviewErrors = (fd) => {
    const errors = {};
    if (fd.status === 'rejected' && !(fd.rejection_reason || '').trim()) errors.rejection_reason = 'Rejection reason is required.';
    const isPaid = Boolean(fd.is_paid);
    if (fd.status === 'approved' && isPaid) {
      if (fd.club_payment_method !== 'stripe' && !existingReimbursementUrl && !(fd.reimbursement_receipt_file instanceof File)) {
        errors.reimbursement_receipt = 'Upload club payment proof when marked as paid.';
      }
      if (!(fd.club_payment_method || '').trim()) errors.club_payment_method = 'Club payment method is required when paid.';
    }
    return errors;
  };

  const collectErrors = (fd, { reviewOnly = false, memberOnly = false } = {}) => {
    const showMgmt = canManageAllClaims(currentUser);
    if (reviewOnly && showMgmt) return collectReviewErrors(fd);
    if (memberOnly || !showMgmt) return collectMemberErrors(fd);
    return { ...collectMemberErrors(fd), ...collectReviewErrors(fd) };
  };

  const focusTabForErrors = (errors) => {
    if (Object.keys(errors).some((key) => MEMBER_VALIDATION_FIELDS.has(key))) setActiveTab(0);
    else if (Object.keys(errors).length > 0) setActiveTab(1);
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, CLAIM_VALIDATION_FIELDS, LARAVEL_FIELD_TO_FORM);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        const formKey = LARAVEL_FIELD_TO_FORM[key] || key;
        if (!CLAIM_VALIDATION_FIELDS.has(formKey)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[formKey] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const showMgmt = canManageAllClaims(currentUser);

    if (isEditMode) {
      const editErrors = collectErrors(formData, {
        reviewOnly: showMgmt && activeTab === 1,
        memberOnly: !showMgmt || activeTab === 0,
      });
      if (Object.keys(editErrors).length > 0) {
        setValidationErrors(editErrors); setShowFieldErrors(true);
        setError(formatFieldErrorsMessage(editErrors)); focusTabForErrors(editErrors); return;
      }
      setValidationErrors({}); setShowFieldErrors(false);
      const claimData = { ...formData };
      if (claimData.company_id) { claimData.company_id = Number(claimData.company_id); delete claimData.company_name; }
      else { delete claimData.company_id; const nm = (claimData.company_name || '').trim(); if (nm) claimData.company_name = nm; else delete claimData.company_name; }
      if (!showMgmt) {
        delete claimData.status; delete claimData.is_claimable; delete claimData.rejection_reason;
        delete claimData.club_payment_method; delete claimData.reimbursement_receipt_file; delete claimData.budget_id;
      }
      ['purchase_payment_date', 'payment_account', 'receipt_number'].forEach((k) => { if (claimData[k] === '') delete claimData[k]; });
      if (String(claimData.rejection_reason || '').trim() === '') delete claimData.rejection_reason;
      const needsMultipart = claimData.receipt_file instanceof File || (showMgmt && claimData.reimbursement_receipt_file instanceof File);
      if (needsMultipart) {
        setPendingUpdateRequest({ kind: 'formdata', url: `/claims/${id}`, fd: buildClaimFormData(claimData, { forUpdate: true }) });
      } else {
        const jsonData = {
          claim_title: buildClaimTitleFromLineItems(claimData.line_items, claimData.claim_title),
          line_items: (claimData.line_items || []).map((row) => ({ name: String(row?.name || '').trim(), amount: row?.amount === '' || row?.amount == null ? null : parseFloat(String(row.amount).replace(/[^0-9.]/g, '')) })).filter((row) => row.name),
          claim_for_self: claimData.claim_for_self,
          claimed_for_user_id: claimData.claim_for_self ? null : (claimData.claimed_for_user_id ? Number(claimData.claimed_for_user_id) : null),
          save_payout_preferences: claimData.save_payout_preferences,
          description: claimData.description, amount: claimData.amount,
          company_id: claimData.company_id, company_name: claimData.company_name,
          purchase_payment_method: claimData.purchase_payment_method,
          purchase_payment_date: claimData.purchase_payment_date,
          receipt_number: claimData.receipt_number, payment_account: claimData.payment_account,
          budget_id: claimData.budget_id ? Number(claimData.budget_id) : null,
        };
        ['purchase_payment_date', 'payment_account', 'receipt_number'].forEach((k) => { if (jsonData[k] === '') delete jsonData[k]; });
        if (claimData.company_id) { jsonData.company_id = Number(claimData.company_id); delete jsonData.company_name; }
        else { delete jsonData.company_id; const nm = (claimData.company_name || '').trim(); if (nm) jsonData.company_name = nm; else delete jsonData.company_name; }
        if (showMgmt) {
          jsonData.status = claimData.status; jsonData.is_claimable = claimData.is_claimable; jsonData.is_paid = claimData.is_paid;
          jsonData.budget_id = claimData.budget_id ? Number(claimData.budget_id) : null;
          jsonData.payment_method = claimData.is_paid ? claimData.club_payment_method : claimData.member_payout_method;
          const rr = String(claimData.rejection_reason || '').trim();
          if (rr) jsonData.rejection_reason = rr; else delete jsonData.rejection_reason;
        } else {
          jsonData.payment_method = claimData.member_payout_method;
        }
        setPendingUpdateRequest({ kind: 'json', url: `/claims/${id}`, jsonData });
      }
      setConfirmUpdateDialogOpen(true); return;
    }

    const errors = collectErrors(formData, { memberOnly: true });
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors); setShowFieldErrors(true);
      setError(formatFieldErrorsMessage(errors)); focusTabForErrors(errors); return;
    }
    setShowFieldErrors(false);
    const claimData = { ...formData };
    if (claimData.company_id) { claimData.company_id = Number(claimData.company_id); delete claimData.company_name; }
    else { delete claimData.company_id; const nm = (claimData.company_name || '').trim(); if (nm) claimData.company_name = nm; else delete claimData.company_name; }
    if (!canManageAllClaims(currentUser)) {
      delete claimData.status; delete claimData.is_claimable; delete claimData.rejection_reason;
      delete claimData.club_payment_method; delete claimData.reimbursement_receipt_file; delete claimData.budget_id;
    }
    ['purchase_payment_date', 'payment_account', 'receipt_number'].forEach((k) => { if (claimData[k] === '') delete claimData[k]; });
    if (String(claimData.rejection_reason || '').trim() === '') delete claimData.rejection_reason;
    try {
      setLoading(true);
      const response = await api.post('/claims', buildClaimFormData(claimData));
      if (response.data) {
        formDraft.clearDraft();
        setSuccessMessage('Claim submitted successfully!');
        setTimeout(() => navigate('/claims', { state: { refreshList: Date.now() } }), 1500);
      }
    } catch (err) {
      handleApiError(err, 'Failed to save claim.');
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    const showMgmtLocal = canManageAllClaims(currentUser);
    const errors = collectErrors(formData, {
      reviewOnly: showMgmtLocal && activeTab === 1,
      memberOnly: !showMgmtLocal || activeTab === 0,
    });
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({ errors, setValidationErrors, setError, setConfirmUpdateDialogOpen, setPendingUpdateRequest, setShowFieldErrors });
      focusTabForErrors(errors); return;
    }
    try {
      setLoading(true); setError(null);
      if (pendingUpdateRequest.kind === 'formdata') await api.post(pendingUpdateRequest.url, pendingUpdateRequest.fd);
      else await api.put(pendingUpdateRequest.url, pendingUpdateRequest.jsonData);
      if (id) reviewFormDraft.clearDraft();
      setSuccessMessage('Claim updated successfully!');
      setTimeout(() => navigate('/claims', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      handleApiError(err, 'Failed to update claim.');
    } finally { setLoading(false); setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }
  };

  const reviewDraftProps   = isEditMode && id ? reviewSubmitDraftProps : submitDraftProps;
  const reviewDraftNotice  = isEditMode && id ? reviewFormDraft : formDraft;
  const showMgmt           = canManageAllClaims(currentUser);
  const isClaimOwner       = !isEditMode || claimOwnerId == null || Number(claimOwnerId) === Number(currentUser?.id);
  const memberStepReadOnly = showMgmt && !isClaimOwner;

  const memberCanSubmit = Boolean(
    ((formData.line_items || []).some((row) => String(row?.name || '').trim()) || formData.claim_title?.trim()) &&
    (formData.claim_for_self || formData.claimed_for_user_id) &&
    formData.description?.trim() && formData.purchase_payment_date &&
    formData.amount !== '' && formData.purchase_payment_method &&
    formData.payment_account?.trim() && formData.member_payout_method &&
    formData.receipt_number?.trim() &&
    (formData.company_id || formData.company_name?.trim()) &&
    (purchasePreviewUrl || formData.receipt_file instanceof File)
  );
  const reviewCanSubmit = !showMgmt || (
    (formData.status !== 'rejected' || Boolean(formData.rejection_reason?.trim())) &&
    (!(formData.status === 'approved' && formData.is_paid) || Boolean(formData.club_payment_method))
  );
  const canSubmit = activeTab === 0 ? (memberStepReadOnly ? false : memberCanSubmit) : reviewCanSubmit;

  const fieldHelper   = (name) => (showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined);
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  useEffect(() => {
    if (showMgmt && isEditMode) setActiveTab(1);
  }, [showMgmt, isEditMode]);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!currentUser) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <FeedbackMessage message="Please log in to access this page." severity="error" />
    </Container>
  );
  if (isEditMode && id && dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading claim form" size={56} variant="primary" />
    </Box>
  );
  const hasLoadedClaim = (formData.line_items || []).some((row) => String(row?.name || '').trim()) || Boolean(formData.claim_title?.trim());
  if (isEditMode && id && error && !hasLoadedClaim) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <FeedbackMessage message={error} severity="error" />
    </Container>
  );
  const editBlockedMsg = isEditMode && id && claimStatus != null
    ? claimEditBlockedMessage(currentUser, { user_id: claimOwnerId, status: claimStatus })
    : null;
  if (editBlockedMsg) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <FeedbackMessage message={editBlockedMsg} severity="error" />
      <Button sx={{ mt: 2 }} onClick={() => navigate('/claims')}>Back to Claims</Button>
    </Container>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
        <Header
          title={isEditMode ? 'Edit claim' : 'Create New claim'}
          subtitle={
            showMgmt && activeTab === 1
              ? 'Approve, pay, and assign budget'
              : (isEditMode ? 'Update your submission' : 'Expense details and receipt')
          }
          icon={ReceiptIcon}
          showBackButton
          onBackClick={() => navigate('/claims')}
          backButtonText="Back to Claims"
        />

        <FeedbackMessage
          message={successMessage} severity="success"
          onClose={() => setSuccessMessage('')}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.1), border: `1px solid ${alpha(SUCCESS, 0.22)}`, color: alpha(SUCCESS, 0.95) }}
        />
        <FeedbackMessage
          message={companiesLoadWarning} severity="warning"
          onClose={() => setCompaniesLoadWarning(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', mb: companiesLoadWarning ? 2 : 0 }}
        />
        <FeedbackMessage
          message={error} severity="error"
          onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.9), border: '1px solid rgba(248,113,113,0.25)', color: '#fca5a5' }}
        />

        <ClaimTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showMgmt={showMgmt}
          claimStatus={claimStatus ?? formData.status}
        />

        {(formDraft.showNotice || (activeTab === 1 && reviewDraftNotice.showNotice)) && (
          <FormDraftNotice
            savedAt={activeTab === 1 && reviewDraftNotice.showNotice ? reviewDraftNotice.restoredAt : formDraft.restoredAt}
            onDiscard={activeTab === 1 && reviewDraftNotice.showNotice ? reviewDraftNotice.discardDraft : formDraft.discardDraft}
            onDismiss={activeTab === 1 && reviewDraftNotice.showNotice ? reviewDraftNotice.dismissNotice : formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', gap: { xs: 0, lg: 3 }, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {activeTab === 0 && (
                <>
                  <MemberRequestTab
                    readOnly={memberStepReadOnly}
                    formData={formData}
                    handleChange={handleChange}
                    handleNativeChange={handleNativeChange}
                    handleLineItemChange={handleLineItemChange}
                    recalcAmountFromLineItems={recalcAmountFromLineItems}
                    validationErrors={validationErrors}
                    fieldHelper={fieldHelper}
                    fieldHasError={fieldHasError}
                    currentUser={currentUser}
                    teamMembers={teamMembers}
                    companies={companies}
                    isEditMode={isEditMode}
                    purchasePreviewUrl={purchasePreviewUrl}
                    savedPurchaseReceiptUrl={savedPurchaseReceiptUrl}
                    savedPurchaseIsPdf={savedPurchaseIsPdf}
                    ocrLoading={ocrLoading}
                    ocrMessage={ocrMessage}
                    ocrIsError={ocrIsError}
                    clearOcrBanner={clearOcrBanner}
                    purchaseDateOpen={purchaseDateOpen}
                    setPurchaseDateOpen={setPurchaseDateOpen}
                    handlePurchaseReceiptChange={handlePurchaseReceiptChange}
                    processPurchaseReceiptFile={processPurchaseReceiptFile}
                    handleRemovePurchaseReceipt={handleRemovePurchaseReceipt}
                  />
                  {!memberStepReadOnly && (
                    <SubmitActionsCard
                      statusText={isEditMode ? 'Save changes when ready.' : 'Submit for review.'}
                      missingRequiredFields={Object.values(collectMemberErrors(formData))}
                      readyStatusText={isEditMode ? 'All required fields are complete. Ready to update claim.' : 'All required fields are complete. Ready to create claim.'}
                      cancelLabel="Cancel"
                      onCancel={() => navigate('/claims')}
                      cancelDisabled={loading}
                      submitLabel={isEditMode ? 'Update Claim' : 'Create Claim'}
                      submitDisabled={loading || !canSubmit}
                      loading={loading}
                      submitStartIcon={<SaveIcon />}
                      {...submitDraftProps}
                    />
                  )}
                  {memberStepReadOnly && showMgmt && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/claims')}
                        sx={{ borderRadius: '10px', color: alpha(TEXT_BODY, 0.75), borderColor: alpha(NEON, 0.2), textTransform: 'none', fontFamily: "'DM Sans',sans-serif" }}
                      >
                        Back to claims
                      </Button>
                      <Button
                        type="button" variant="contained"
                        startIcon={<GavelIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setActiveTab(1)}
                        sx={{
                          borderRadius: '10px', textTransform: 'none',
                          fontFamily: "'Rajdhani',sans-serif", fontWeight: 700,
                          background: alpha(NEON2, 0.18),
                          border: `1px solid ${alpha(NEON2, 0.38)}`,
                          color: NEON2,
                          boxShadow: 'none',
                          '&:hover': { background: alpha(NEON2, 0.24), boxShadow: 'none' },
                        }}
                      >
                        Go to Review
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {activeTab === 1 && showMgmt && (
                <>
                  <ReviewProcessTab
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    fieldHelper={fieldHelper}
                    fieldHasError={fieldHasError}
                    budgets={budgets}
                    budgetsLoadWarning={budgetsLoadWarning}
                    purchasePreviewUrl={purchasePreviewUrl}
                    reimbursementPreviewUrl={reimbursementPreviewUrl}
                    existingReimbursementUrl={existingReimbursementUrl}
                    savedReimbursementIsPdf={savedReimbursementIsPdf}
                    handleReimbursementReceiptChange={handleReimbursementReceiptChange}
                    processReimbursementReceiptFile={processReimbursementReceiptFile}
                    handleRemoveReimbursementReceipt={handleRemoveReimbursementReceipt}
                    isEditMode={isEditMode}
                    id={id}
                    currentUser={currentUser}
                    companies={companies}
                    teamMembers={teamMembers}
                  />
                  <SubmitActionsCard
                    statusText="Save review when done."
                    missingRequiredFields={Object.values(collectReviewErrors(formData))}
                    readyStatusText="All required review fields are complete. Ready to submit review."
                    cancelLabel="Cancel"
                    onCancel={() => navigate('/claims')}
                    cancelDisabled={loading}
                    submitLabel={isEditMode ? 'Update Review' : 'Submit Review'}
                    submitDisabled={loading || !canSubmit}
                    loading={loading}
                    submitStartIcon={<GavelIcon />}
                    {...reviewDraftProps}
                  />
                </>
              )}
            </Box>
          </Box>
        </form>

        <ConfirmUpdateDialog
          open={confirmUpdateDialogOpen}
          onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
          onConfirm={confirmUpdate}
          title="Confirm Update"
          variant="edit"
          itemType="claim"
          itemName={buildClaimTitleFromLineItems(formData.line_items, formData.claim_title)}
          description="This action will update the claim details and cannot be undone."
          confirmLabel={isEditMode ? 'Update Claim' : 'Submit Claim'}
          loading={loading}
          confirmDisabled={!canSubmit}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default ClaimForm;
